import { useState, useEffect, useMemo } from "react";
import { Navigate } from "react-router-dom";
import { useNotification } from "../../context/NotificationContext";
import { useAuth } from "../../context/AuthContext";
import LoginForm from "./components/LoginForm";
import RegisterForm from "./components/RegisterForm";
import ForgotPasswordForm from "./components/ForgotPasswordForm";
import ResendVerificationForm from "./components/ResendVerificationForm";
import ResetPasswordForm from "./components/ResetPasswordForm";

const FORM_META = {
  login: {
    eyebrow: "Connexion",
    title: "Reprends ta progression et continue à jouer",
    description:
      "Retrouve tes scores, tes badges et ton avatar en quelques secondes.",
  },
  register: {
    eyebrow: "Inscription",
    title: "Crée ton compte et entre dans l'arène PixyQuiz",
    description:
      "Joue, progresse, débloque des récompenses et garde ton historique de partie.",
  },
  forgot: {
    eyebrow: "Récupération",
    title: "Récupère ton accès en toute simplicité",
    description:
      "On t'aide à reprendre la partie sans toucher à la logique existante.",
  },
  resend: {
    eyebrow: "Vérification",
    title: "Renvoie ton lien d'activation",
    description:
      "Réactive ton compte pour retrouver toute ta progression.",
  },
  reset: {
    eyebrow: "Sécurité",
    title: "Choisis un nouveau mot de passe",
    description: "Reviens vite en jeu avec un accès sécurisé et propre.",
  },
};

const PRODUCT_PILLARS = [
  "Rooms en temps réel",
  "Badges, avatars, classements",
];

export default function Authentification() {
  const [activeForm, setActiveForm] = useState("login");
  const { showNotification } = useNotification();
  const { canAccessMemberArea } = useAuth();

  useEffect(() => {
    const syncFormFromHash = () => {
      const hash = window.location.hash;
      if (hash === "#inscription") setActiveForm("register");
      else if (hash === "#motdepasse") setActiveForm("forgot");
      else if (hash === "#resend") setActiveForm("resend");
      else if (hash.startsWith("#reset")) setActiveForm("reset");
      else setActiveForm("login");

      if (hash.includes("#success") && hash.includes("verified")) {
        showNotification("success", "Votre compte a bien été vérifié.");
        window.location.hash = "#connexion";
        setActiveForm("login");
      }

      if (hash.includes("#erreur")) {
        const msg = new URLSearchParams(window.location.hash.split("?")[1]).get("msg");
        let message = "Une erreur est survenue lors de la verification.";
        if (msg === "token_invalide") message = "Lien invalide.";
        if (msg === "token_expire") message = "Lien expire, veuillez le renvoyer.";
        showNotification("error", message);
        window.location.hash = "#connexion";
        setActiveForm("login");
      }
    };

    const handleSwitchAuthForm = (event) => {
      const target = event.detail;
      if (target === "connexion" || target === "login") {
        window.location.hash = "#connexion";
        setActiveForm("login");
      }
      if (target === "inscription" || target === "register") {
        window.location.hash = "#inscription";
        setActiveForm("register");
      }
    };

    syncFormFromHash();
    window.addEventListener("hashchange", syncFormFromHash);
    window.addEventListener("switchAuthForm", handleSwitchAuthForm);

    return () => {
      window.removeEventListener("hashchange", syncFormFromHash);
      window.removeEventListener("switchAuthForm", handleSwitchAuthForm);
    };
  }, [showNotification]);

  const toggleForm = (target) => {
    setActiveForm(target);
    if (target === "login") window.location.hash = "#connexion";
    if (target === "register") window.location.hash = "#inscription";
    if (target === "forgot") window.location.hash = "#motdepasse";
    if (target === "resend") window.location.hash = "#resend";
  };

  const meta = useMemo(
    () => FORM_META[activeForm] || FORM_META.login,
    [activeForm]
  );

  if (canAccessMemberArea) {
    return <Navigate to="/profile" replace />;
  }

  return (
    <div className="min-h-screen bg-[linear-gradient(180deg,_#f8fff8,_#e9f7ff)] px-4 py-8 sm:px-6 lg:px-8">
      <div className="mx-auto grid min-h-[calc(100vh-4rem)] max-w-7xl overflow-hidden rounded-[36px] border border-emerald-100 bg-white shadow-[0_24px_80px_rgba(24,48,74,0.08)] xl:grid-cols-[1.05fr_0.95fr]">

        <aside className="relative hidden overflow-hidden border-r border-emerald-100 xl:block">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(95,181,111,0.12),transparent_40%),radial-gradient(circle_at_bottom_right,rgba(47,157,226,0.10),transparent_40%)]" />
          <div className="relative flex h-full flex-col justify-between p-10 text-pixy-ink">
            <div>
              <div className="inline-flex items-center rounded-full border border-pixy-blue/20 bg-pixy-sky px-4 py-2 text-xs font-semibold uppercase tracking-[0.32em] text-pixy-blue">
                PixyQuiz Account
              </div>
              <h1 className="mt-8 max-w-xl text-5xl font-black leading-tight text-pixy-ink">
                Connecte-toi et reviens dans une vraie arène de quiz en ligne.
              </h1>
              <p className="mt-6 max-w-lg text-base leading-8 text-slate-600">
                Garde tes scores, fais monter ton profil, débloque des badges et
                garde la main sur toute ta progression depuis un espace plus
                propre et plus moderne.
              </p>
            </div>

            <div className="space-y-5">
              <div className="grid gap-3">
                {PRODUCT_PILLARS.map((pillar) => (
                  <div
                    key={pillar}
                    className="rounded-2xl border border-emerald-100 bg-white px-4 py-4 text-sm font-semibold text-pixy-ink shadow-sm"
                  >
                    {pillar}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </aside>

        <section className="flex items-center justify-center px-4 py-8 sm:px-8 lg:px-10">
          <div className="form-wrapper w-full max-w-xl rounded-[30px] border border-emerald-100 bg-white p-6 shadow-[0_8px_40px_rgba(24,48,74,0.08)] sm:p-8 lg:p-10">
            <div className="mb-8">
              <div className="text-xs font-semibold uppercase tracking-[0.3em] text-pixy-blue">
                {meta.eyebrow}
              </div>
              <h2 className="mt-3 text-3xl font-black text-pixy-ink sm:text-4xl">
                {meta.title}
              </h2>
              <p className="mt-3 max-w-lg text-sm leading-7 text-slate-600">
                {meta.description}
              </p>
            </div>

            {activeForm === "login" && <LoginForm onSwitch={toggleForm} />}
            {activeForm === "register" && <RegisterForm onSwitch={toggleForm} />}
            {activeForm === "forgot" && <ForgotPasswordForm onSwitch={toggleForm} />}
            {activeForm === "resend" && <ResendVerificationForm onSwitch={toggleForm} />}
            {activeForm === "reset" && <ResetPasswordForm onSwitch={toggleForm} />}
          </div>
        </section>
      </div>
    </div>
  );
}
